import t from '@rcm/i18n-client';
import classNames from 'classnames';
import { ChangeHandler, RawFieldRenderer, SelectField, SelectOption } from 'forms';
import noop from 'lodash/noop';
import range from 'lodash/range';
import React, { useCallback } from 'react';
import '../../../styles/credit-card.scss';

interface Props {
  disabled?: boolean;
  error?: any;
  expirationMonth?: number;
  expirationYear?: number;
  onExpirationMonthChange?: ChangeHandler<number>;
  onExpirationYearChange?: ChangeHandler<number>;
}

const numberToOption = (value: number) => ({ value, label: `${value}` } as SelectOption<number>);

const CURRENT_YEAR = new Date().getUTCFullYear();

const EMPTY_OPTION = { label: '', value: -1 } as SelectOption<number>;

const EXPIRATION_MONTH_OPTIONS: readonly SelectOption[] = [EMPTY_OPTION, ...range(1, 13).map(numberToOption)];

const EXPIRATION_YEAR_OPTIONS: readonly Readonly<SelectOption<number>>[] = [
  EMPTY_OPTION,
  ...range(CURRENT_YEAR, CURRENT_YEAR + 21).map(numberToOption),
];

/**
 * Credit card expiration date month/year field pair.
 *
 * The current date is used to provide a list of available options up to 21
 * years into the future. Beyond that, no internal date processing is performed,
 * and the year/month values are handled as simple strings.
 */
const CreditCardExpirationDateFields = ({
  disabled,
  error,
  expirationMonth,
  expirationYear,
  onExpirationMonthChange = noop,
  onExpirationYearChange = noop,
}: Props) => {
  const handleExpirationMonthChange: ChangeHandler<number> = useCallback(
    (value, oldValue, name, event) => {
      return onExpirationMonthChange(value === -1 ? null : value, oldValue === -1 ? null : oldValue, name, event);
    },
    [onExpirationMonthChange]
  );

  const handleExpirationYearChange: ChangeHandler<number> = useCallback(
    (value, oldValue, name, event) => {
      return onExpirationYearChange(value === -1 ? null : value, oldValue === -1 ? null : oldValue, name, event);
    },
    [onExpirationYearChange]
  );

  return (
    <div className={classNames('form-group', 'ccExpDate', { 'input-error': error })}>
      <label>
        <span>{t('Expiration Date')}</span>
      </label>
      <SelectField
        autoComplete="off"
        disabled={disabled}
        name="creditCard[expirationMonth]" // Used by tests
        onValueChange={handleExpirationMonthChange}
        options={EXPIRATION_MONTH_OPTIONS}
        Renderer={RawFieldRenderer}
        value={expirationMonth ?? -1}
      />
      <SelectField
        autoComplete="off"
        disabled={disabled}
        name="creditCard[expirationYear]" // Used by tests
        onValueChange={handleExpirationYearChange}
        options={EXPIRATION_YEAR_OPTIONS}
        Renderer={RawFieldRenderer}
        value={expirationYear ?? -1}
      />
    </div>
  );
};

export default CreditCardExpirationDateFields;
